import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTimezone } from "@/contexts/TimezoneContext";
import { convertShowTimes, BASE_TZ, zoneLabel } from "@/lib/timezone";
import TimezoneSelector from "@/components/TimezoneSelector";
import StreakBar from "@/components/StreakBar";
import FollowHostButton from "@/components/FollowHostButton";
import { toast } from "sonner";
import { Bell, BellOff, Calendar, ExternalLink, Trash2, Radio, Bookmark, Send, Star } from "lucide-react";

const DAY_SHORT = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

function airingDays(s) {
  if (s.is_special && s.special_frequency === "daily") return [0, 1, 2, 3, 4, 5, 6];
  if (Array.isArray(s.days_of_week) && s.days_of_week.length > 0) return s.days_of_week;
  return [s.day_of_week ?? 0];
}

export default function Watchlist() {
  const { user, loading: authLoading, refresh, logout } = useAuth();
  const { timezone, isBase, setTimezone } = useTimezone();
  const [shows, setShows] = useState([]);
  const [savedHosts, setSavedHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [discordCfg, setDiscordCfg] = useState({ bot_enabled: false, invite_url: "" });
  const [streakKey, setStreakKey] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const [showsResp, hostsResp] = await Promise.all([
        api.get("/user/saved-shows"),
        api.get("/user/saved-hosts"),
      ]);
      setShows(showsResp.data);
      setSavedHosts(hostsResp.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load watchlist");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/config/discord");
        setDiscordCfg(data);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user]);

  // Sync selected timezone back to server whenever the user changes it.
  useEffect(() => {
    if (!user) return;
    if (user.preferred_timezone === timezone) return;
    (async () => {
      try {
        await api.put("/user/preferences", { preferred_timezone: timezone });
        await refresh();
      } catch {}
    })();
  }, [timezone, user]);

  useEffect(() => {
    if (user?.preferred_timezone && user.preferred_timezone !== timezone) {
      setTimezone(user.preferred_timezone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (authLoading) return <div className="p-12 text-[var(--ff-text-dim)] font-mono-ui text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.is_banned) return <Navigate to="/banned" replace />;

  const toggleNotify = async () => {
    setSaving(true);
    try {
      await api.put("/user/preferences", { notify_via_discord: !user.notify_via_discord });
      await refresh();
      toast.success(user.notify_via_discord ? "DM reminders disabled" : "DM reminders enabled");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const removeShow = async (id, viaHost) => {
    if (viaHost) {
      toast("This show comes from a host you follow", {
        description: `Unfollow ${viaHost} from the host card below to hide their shows.`,
      });
      return;
    }
    try {
      await api.delete(`/user/saved-shows/${id}`);
      setShows((prev) => prev.filter((s) => s.id !== id));
      toast.success("Removed from watchlist");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const markWatched = async (id) => {
    try {
      const { data } = await api.post(`/user/watched/${id}`, { source: "manual" });
      if (data.counted) {
        toast.success("Marked as watched 🔥");
        setStreakKey((k) => k + 1);
      } else {
        toast("Already counted today", { description: "Come back next week to grow your streak." });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const sendTestDm = async () => {
    try {
      await api.post("/user/test-dm");
      toast.success("Test DM sent — check your Discord");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send DM");
    }
  };

  const icsHref = (id) => `${API_BASE}/shows/${id}/ics`;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24" data-testid="watchlist-page">
      {/* Profile bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10 pb-8 border-b border-[var(--ff-border)]">
        <div className="flex items-center gap-4">
          {user.discord_avatar ? (
            <img src={user.discord_avatar} alt={user.discord_username} className="w-16 h-16 rounded-full ring-2 ring-[#5865F2]" data-testid="user-avatar" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-[var(--ff-red)] text-white flex items-center justify-center font-display text-2xl">
              {(user.name || "?")[0].toUpperCase()}
            </div>
          )}
          <div>
            <div className="label-eyebrow mb-1 text-[var(--ff-red)]">▮ MY WATCHLIST</div>
            <h1 className="font-display text-4xl leading-none" data-testid="user-name">{user.discord_username || user.name}</h1>
            {user.discord_id && <div className="font-mono-ui text-[0.65rem] text-[var(--ff-text-dim)] mt-1 uppercase tracking-[0.15em]">Signed in with Discord</div>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <TimezoneSelector />
          <button onClick={logout} className="btn-ghost text-xs" data-testid="watchlist-logout-btn">Sign Out</button>
        </div>
      </div>

      {/* Streak badges */}
      <StreakBar reloadKey={streakKey} />

      {/* Notification settings */}
      <div className="surface-card p-6 mb-10 grid md:grid-cols-2 gap-6" data-testid="notification-settings">
        <div>
          <div className="label-eyebrow mb-2 text-[var(--ff-red)]">▮ REMINDERS</div>
          <p className="text-sm text-[var(--ff-text-dim)] mb-4">
            Get a Discord DM ~15 minutes before every saved show, plus a &ldquo;going live now&rdquo; ping when it starts. Add any show to your calendar with one tap.
          </p>
          <button
            onClick={toggleNotify}
            disabled={saving || !user.discord_id || !discordCfg.bot_enabled}
            className={`inline-flex items-center gap-2 px-4 py-2.5 border font-mono-ui text-xs uppercase tracking-[0.15em] transition-colors ${user.notify_via_discord ? "bg-[#5865F2] text-white border-[#5865F2]" : "border-[var(--ff-border)] text-white hover:border-white"} disabled:opacity-40 disabled:cursor-not-allowed`}
            data-testid="toggle-dm-btn"
          >
            {user.notify_via_discord ? <Bell size={14} /> : <BellOff size={14} />}
            {user.notify_via_discord ? "DM Reminders ON" : "Enable DM Reminders"}
          </button>
          {user.notify_via_discord && (
            <button
              onClick={sendTestDm}
              className="ml-3 inline-flex items-center gap-2 px-4 py-2.5 border border-[var(--ff-border)] text-white hover:border-[var(--ff-red)] font-mono-ui text-xs uppercase tracking-[0.15em] transition-colors"
              data-testid="test-dm-btn"
            >
              <Send size={12} /> Send Test DM
            </button>
          )}
        </div>
        <div className="border-l border-[var(--ff-border)] pl-6">
          <div className="label-eyebrow mb-2">▮ HEADS UP</div>
          <ul className="text-xs text-[var(--ff-text-dim)] space-y-2 font-mono-ui leading-relaxed">
            <li>• DM reminders require you to share a Discord server with our bot.</li>
            {discordCfg.invite_url && (
              <li>
                • <a href={discordCfg.invite_url} target="_blank" rel="noreferrer" className="text-[var(--ff-red)] hover:underline" data-testid="bot-invite-link">Join our Discord →</a>
              </li>
            )}
            <li>• Calendar (.ics) downloads work for everyone — no Discord required.</li>
            <li>• All show times shown in <span className="text-white">{zoneLabel(timezone)}</span>{!isBase && " · Baseline: US Eastern"}.</li>
          </ul>
        </div>
      </div>

      {/* Followed hosts */}
      {savedHosts.length > 0 && (
        <div className="mb-10" data-testid="followed-hosts">
          <div className="label-eyebrow mb-4 flex items-center gap-2"><Star size={14} className="text-[#FFD60A]" /> HOSTS I FOLLOW · {savedHosts.length}</div>
          <div className="flex flex-wrap gap-2">
            {savedHosts.map((h) => (
              <div key={h.name} className="flex items-center gap-2 border border-[var(--ff-border)] p-2 pr-3">
                {h.avatar_url ? (
                  <img src={h.avatar_url} alt={h.name} className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[var(--ff-red)] text-white text-xs flex items-center justify-center font-display">
                    {h.name[0]}
                  </div>
                )}
                <Link to={`/hosts/${encodeURIComponent(h.name)}`} className="font-mono-ui text-xs uppercase tracking-[0.12em] hover:text-[var(--ff-red)]">
                  {h.name}
                </Link>
                <FollowHostButton hostName={h.name} size="sm" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Watchlist grid */}
      <div className="flex items-center justify-between mb-6">
        <div className="label-eyebrow flex items-center gap-2"><Bookmark size={14} /> SAVED SHOWS · {shows.length}</div>
        <Link to="/shows" className="btn-ghost text-xs" data-testid="browse-shows-link">Browse All Shows →</Link>
      </div>

      {loading ? (
        <div className="text-[var(--ff-text-dim)] font-mono-ui text-sm">Loading your watchlist...</div>
      ) : shows.length === 0 ? (
        <div className="surface-card p-12 text-center" data-testid="empty-watchlist">
          <Radio size={32} className="mx-auto text-[var(--ff-text-dim)] mb-4" />
          <div className="font-display text-2xl mb-2">NO SAVED SHOWS YET.</div>
          <p className="text-[var(--ff-text-dim)] text-sm mb-6">Tap the bookmark on any show — or follow a host on the Hosts page.</p>
          <div className="flex justify-center gap-3">
            <Link to="/shows" className="btn-primary" data-testid="empty-browse-btn">Browse Shows →</Link>
            <Link to="/hosts" className="btn-ghost">Meet the Hosts →</Link>
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="watchlist-grid">
          {shows.map((s) => {
            const base = airingDays(s);
            let days = base;
            let start = s.start_time;
            let end = s.end_time;
            if (!isBase && s.start_time && s.end_time) {
              const anchor = base[0];
              const conv = convertShowTimes(anchor, s.start_time, s.end_time, timezone);
              const shift = ((conv.day - anchor) % 7 + 7) % 7;
              days = base.map((d) => (d + shift) % 7).sort((a, b) => a - b);
              start = conv.start_time;
              end = conv.end_time;
            }
            const viaHost = s.via_host;
            return (
              <div key={s.id} className="surface-card p-5 flex flex-col gap-3" data-testid={`watchlist-item-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <Link to={`/shows/${s.id}`} className="flex-1 min-w-0">
                    <div className="font-display text-xl leading-tight truncate">{s.title}</div>
                    <div className="font-mono-ui text-[0.65rem] text-[var(--ff-text-dim)] mt-1 uppercase tracking-[0.12em]">
                      {days.map((d) => DAY_SHORT[d]).join(" · ")} / {start}–{end}
                    </div>
                    {viaHost && (
                      <div className="font-mono-ui text-[0.6rem] text-[#FFD60A] mt-1 uppercase tracking-[0.12em] flex items-center gap-1" data-testid={`via-host-${s.id}`}>
                        <Star size={9} fill="currentColor" /> via {viaHost}
                      </div>
                    )}
                  </Link>
                  <button
                    onClick={() => removeShow(s.id, viaHost)}
                    className="text-[var(--ff-text-dim)] hover:text-[var(--ff-red)] transition-colors flex-shrink-0"
                    title={viaHost ? `Following ${viaHost} — unfollow to remove` : "Remove from watchlist"}
                    data-testid={`remove-${s.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                {s.description && <p className="text-xs text-[var(--ff-text-dim)] line-clamp-2">{s.description}</p>}
                <div className="flex items-center gap-2 pt-2 border-t border-[var(--ff-border)]">
                  <button
                    onClick={() => markWatched(s.id)}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-[var(--ff-border)] hover:border-[#FFD60A] hover:text-[#FFD60A] font-mono-ui text-[0.6rem] uppercase tracking-[0.12em] transition-colors"
                    data-testid={`watched-${s.id}`}
                  >
                    🔥 Watched
                  </button>
                  <a href={icsHref(s.id)} className="btn-ghost text-[0.6rem]" data-testid={`ics-${s.id}`}>
                    <Calendar size={11} /> .ICS
                  </a>
                  {s.youtube_url && (
                    <a href={s.youtube_url} target="_blank" rel="noreferrer" className="btn-primary text-[0.6rem] flex-1 justify-center" data-testid={`yt-${s.id}`}>
                      <ExternalLink size={11} /> Live
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
